import React, { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import { createEvent } from '../services/eventService';
import { toast } from 'react-toastify';

interface EventFormData {
  title: string;
  sub_title: string;
  notification_enabled: boolean;
  Branding: string;
  register_start_date: string;
  register_end_date: string;
  vote_end_date: string;
  event_date: string;
  min_select_users: number;
  max_select_users: number;
  round: number;
  location_name: string;
  country: string;
  city: string;
  postal_code: string;
  street: string;
  house_number: string;
}

const initialFormData: EventFormData = {
  title: '',
  sub_title: '',
  notification_enabled: false,
  Branding: 'MatchMaker',
  register_start_date: '',
  register_end_date: '',
  vote_end_date: '',
  event_date: '',
  min_select_users: 1,
  max_select_users: 10,
  round: 3,
  location_name: '',
  country: '',
  city: '',
  postal_code: '',
  street: '',
  house_number: ''
};

interface EventFormProps {
  onEventCreated: () => void;
}

export const EventForm: React.FC<EventFormProps> = ({ onEventCreated }) => {
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [minDateLimits, setMinDateLimits] = useState({
    register_start_date: '',
    register_end_date: '',
    vote_end_date: '',
    event_date: ''
  });

  // Initialize date constraints on component mount
  useEffect(() => {
    const now = new Date();
    const currentDateTimeString = now.toISOString().slice(0, 16);
    
    setMinDateLimits({
      register_start_date: currentDateTimeString,
      register_end_date: currentDateTimeString,
      vote_end_date: currentDateTimeString,
      event_date: currentDateTimeString
    });
  }, []);

  // Helper function to add days to a date
  const addDays = (dateStr: string, days: number): string => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 16);
  };

  // Handle input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });

    // Update date constraints when dates change
    if (id === 'register_start_date' && value) {
      setMinDateLimits(prev => ({
        ...prev,
        register_end_date: addDays(value, 1),
        vote_end_date: addDays(value, 2),
        event_date: addDays(value, 3)
      }));
    } else if (id === 'register_end_date' && value) {
      setMinDateLimits(prev => ({
        ...prev,
        vote_end_date: addDays(value, 1),
        event_date: addDays(value, 2)
      }));
    } else if (id === 'vote_end_date' && value) {
      setMinDateLimits(prev => ({
        ...prev,
        event_date: addDays(value, 1)
      }));
    } else if (id === 'min_select_users') {
      // Ensure max_select_users is always greater than min_select_users
      const minVal = parseInt(value, 10);
      if (!isNaN(minVal) && minVal >= 2) {
        const maxVal = parseInt(formData.max_select_users.toString(), 10);
        if (maxVal <= minVal) {
          setFormData(prev => ({
            ...prev,
            max_select_users: minVal + 1
          }));
        }
      }
    } else if (id === 'max_select_users') {
      // Ensure max_select_users is always greater than min_select_users
      const maxVal = parseInt(value, 10);
      const minVal = parseInt(formData.min_select_users.toString(), 10);
      if (!isNaN(maxVal) && !isNaN(minVal) && maxVal <= minVal) {
        setFormData(prev => ({
          ...prev,
          max_select_users: minVal + 1
        }));
      }
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setFormData({
      ...formData,
      [id]: checked
    });
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Convert string dates to ISO format
      const formattedData = {
        ...formData,
        register_start_date: new Date(formData.register_start_date).toISOString(),
        register_end_date: new Date(formData.register_end_date).toISOString(),
        vote_end_date: new Date(formData.vote_end_date).toISOString(),
        event_date: new Date(formData.event_date).toISOString(),
        min_select_users: Number(formData.min_select_users),
        max_select_users: Number(formData.max_select_users),
        round: Number(formData.round)
      };

      const response = await createEvent(formattedData);
      
      // Consider it successful if we get a response (even if it's empty)
      // The API returns 200 OK which means it was successful
      setFormData(initialFormData);
      onEventCreated();
      
      // Show success toast
      toast.success('Event was successfully created!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      
      // Close modal - improved modal closing logic
      closeModal();
    } catch (err) {
      console.error('Error creating event:', err);
      setError('An error occurred while creating the event.');
      toast.error('An error occurred while creating the event.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to close the modal
  const closeModal = () => {
    const modal = document.getElementById('eventModal');
    if (modal) {
      // Try to use Bootstrap 5 API if available
      if (window.bootstrap && window.bootstrap.Modal) {
        const modalInstance = window.bootstrap.Modal.getInstance(modal);
        if (modalInstance) {
          modalInstance.hide();
        }
      } else {
        // Fallback method if Bootstrap JS is not properly loaded
        const closeButton = modal.querySelector('.btn-close');
        if (closeButton) {
          (closeButton as HTMLElement).click();
        }
      }
    }
  };

  return (
    <form id="eventForm" onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {/* Event Details */}
      <h5 className="mb-3">Event Details</h5>
      <div className="row">
        <div className="col-md-12 mb-3">
          <label htmlFor="title" className="form-label">Title</label>
          <input 
            type="text" 
            className="form-control" 
            id="title" 
            value={formData.title}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-12 mb-3">
          <label htmlFor="sub_title" className="form-label">Subtitle</label>
          <textarea 
            className="form-control" 
            id="sub_title" 
            rows={3} 
            value={formData.sub_title}
            onChange={handleInputChange}
            required
          ></textarea>
        </div>

        {/* Notification Option */}
        <div className="col-md-6">
          <input 
            className="form-check-input" 
            type="checkbox" 
            id="notification_enabled" 
            checked={formData.notification_enabled}
            onChange={handleCheckboxChange}
          />
          <label className="form-check-label ms-2" htmlFor="notification_enabled">
            Notification over MatchMaker
          </label>
        </div>

        <div className="col-md-6">
          <label className="small mb-1" htmlFor="Branding">Branding</label>
          <select 
            className="form-control" 
            id="Branding"
            value={formData.Branding}
            onChange={handleInputChange}
          >
            <option value="MatchMaker">MatchMaker</option>
            <option value="Managing Partners' Speed Dating">Managing Partners' Speed Dating</option>
          </select>
        </div>
      </div>

      <hr />
      <h5 className="mb-3">Event Dates</h5>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="register_start_date" className="form-label">Registration Start Date</label>
          <input 
            type="datetime-local" 
            className="form-control" 
            id="register_start_date"
            min={minDateLimits.register_start_date}
            value={formData.register_start_date}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="register_end_date" className="form-label">Registration End Date</label>
          <input 
            type="datetime-local" 
            className="form-control" 
            id="register_end_date"
            min={minDateLimits.register_end_date}
            value={formData.register_end_date}
            onChange={handleInputChange}
            required 
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="vote_end_date" className="form-label">Vote End Date</label>
          <input 
            type="datetime-local" 
            className="form-control" 
            id="vote_end_date"
            min={minDateLimits.vote_end_date}
            value={formData.vote_end_date}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="event_date" className="form-label">Event Date</label>
          <input 
            type="datetime-local" 
            className="form-control" 
            id="event_date"
            min={minDateLimits.event_date}
            value={formData.event_date}
            onChange={handleInputChange}
            required 
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 mb-3">
          <label htmlFor="min_select_users" className="form-label">Min. Participants</label>
          <input 
            type="number" 
            className="form-control" 
            id="min_select_users"
            min="2"
            value={formData.min_select_users}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="max_select_users" className="form-label">Max. Participants</label>
          <input 
            type="number" 
            className="form-control" 
            id="max_select_users"
            min={parseInt(formData.min_select_users.toString(), 10) + 1}
            value={formData.max_select_users}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="round" className="form-label">Rounds</label>
          <input 
            type="number" 
            className="form-control" 
            id="round"
            value={formData.round}
            onChange={handleInputChange}
            required 
          />
        </div>
      </div>

      {/* Location Details */}
      <hr />
      <h5 className="mb-3">Location Details</h5>
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="location_name" className="form-label">Location Name</label>
          <input 
            type="text" 
            className="form-control" 
            id="location_name"
            value={formData.location_name}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="country" className="form-label">Country</label>
          <input 
            type="text" 
            className="form-control" 
            id="country"
            value={formData.country}
            onChange={handleInputChange}
            required 
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="city" className="form-label">City</label>
          <input 
            type="text" 
            className="form-control" 
            id="city"
            value={formData.city}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="postal_code" className="form-label">Postal Code</label>
          <input 
            type="text" 
            className="form-control" 
            id="postal_code"
            value={formData.postal_code}
            onChange={handleInputChange}
            required 
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-8 mb-3">
          <label htmlFor="street" className="form-label">Street</label>
          <input 
            type="text" 
            className="form-control" 
            id="street"
            value={formData.street}
            onChange={handleInputChange}
            required 
          />
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="house_number" className="form-label">House Number</label>
          <input 
            type="text" 
            className="form-control" 
            id="house_number"
            value={formData.house_number}
            onChange={handleInputChange}
            required 
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-3">
        <button 
          type="submit" 
          className="btn btn-success" 
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};
